import React, { useState } from 'react';
import { DatePicker, DefaultButton, Dropdown, IDropdownOption, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import moment from 'moment';
import { useStore } from '../../../../stores/root';
import { IOperationItem } from '../types';
import Label from '../../../typography/label';
import Text from '../../../typography/text';
import GeneralService from '../../../../services/general';
import ErrorService from '../../../../services/general/error';
import ShipSchedulesService from '../../../../services/data/ships/shipSchedules';
import OrderTrackingService from '../../../../services/orders/trackings/orderTracking';
import { faCheck, faCircleCheck, faClockRotateLeft, faLocationDot, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IQuickStatusModalProps {
    item: IOperationItem;
    isOpen: boolean;
    onDismiss(refresh?: boolean): void;
}

const SHIP_STATUS_OPTIONS: IDropdownOption[] = [
    { key: 'scheduled', text: 'Scheduled' },
    { key: 'sailing', text: 'Sailing / Departed' },
    { key: 'arrived', text: 'Docked / Arrived at Port' },
    { key: 'completed', text: 'Completed' },
];

const TRACKING_STATUS_OPTIONS: IDropdownOption[] = [
    { key: 'scheduled', text: 'Scheduled' },
    { key: 'in_progress', text: 'In Progress' },
    { key: 'completed', text: 'Completed' },
];

const QuickStatusModal: React.FC<IQuickStatusModalProps> = ({ item, isOpen, onDismiss }) => {
    const { banner } = useStore();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const isShip = item.itemType === 'ship';

    // Start with current item status so users must consciously pick new status, preventing accidental arrival/docking
    const defaultStatus = item.status || (isShip ? 'scheduled' : 'in_progress');

    const [status, setStatus] = useState<string>(defaultStatus);
    const [actualDate, setActualDate] = useState<Date>(new Date());
    const [newEstimatedArrival, setNewEstimatedArrival] = useState<Date | undefined>(() => {
        if (item.estimatedArrival && moment(item.estimatedArrival, 'DD/MM/YYYY').isValid()) {
            return moment(item.estimatedArrival, 'DD/MM/YYYY').toDate();
        }
        return undefined;
    });
    // Kosongkan secara default agar user mengisi catatan progres baru yang akan di-append
    const [notes, setNotes] = useState<string>('');

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const dateIso = moment(actualDate).toISOString();

            // Append catatan progres baru ke catatan sebelumnya tanpa menghapus yang lama
            let finalNotes = (item.notes || '').trim();
            if (notes && notes.trim() !== '') {
                const timestamp = moment().format('DD/MM/YYYY HH:mm');
                const statusOption = (isShip ? SHIP_STATUS_OPTIONS : TRACKING_STATUS_OPTIONS).find(o => o.key === status);
                const statusText = statusOption?.text || status;
                const newEntry = `[${timestamp} | ${statusText}] ${notes.trim()}`;
                finalNotes = finalNotes !== '' ? `${finalNotes}\n---\n${newEntry}` : newEntry;
            }

            if (isShip) {
                const fd = new FormData();
                fd.append('status', status);
                if (status === 'sailing') {
                    fd.append('actualDeparture', dateIso);
                    fd.append('actualArrival', 'null');
                    if (newEstimatedArrival) {
                        fd.append('estimatedArrival', moment(newEstimatedArrival).toISOString());
                    }
                } else if (status === 'scheduled') {
                    fd.append('actualDeparture', 'null');
                    fd.append('actualArrival', 'null');
                } else if (status === 'arrived' || status === 'completed') {
                    fd.append('actualArrival', dateIso);
                }

                if (finalNotes) {
                    fd.append('notes', finalNotes);
                }

                await ShipSchedulesService.update(item.rawId, fd);
            } else if (item.orderId) {
                const fd = new FormData();
                const trackingType = item.rawObject?.type || (item.activityType === 'unloading' ? 'unload' : (item.activityType === 'loading' ? 'load' : 'truck'));
                fd.append('type', trackingType);
                fd.append('status', status);

                const safeAddress = item.locationText && item.locationText !== 'ededed' ? item.locationText : '-';
                fd.append('address', safeAddress);
                if (item.activityType === 'dooring') {
                    fd.append('originAddress', safeAddress);
                    fd.append('destinationAddress', item.destinationAddress || safeAddress);
                }

                if (item.activityType === 'loading' || item.activityType === 'unloading') {
                    if (status === 'in_progress') {
                        fd.append('actualStartDate', dateIso);
                    } else if (status === 'completed') {
                        fd.append('actualEndDate', dateIso);
                        if (!item.actualDate) {
                            fd.append('actualStartDate', dateIso);
                        }
                    }
                } else if (item.activityType === 'dooring') {
                    if (status === 'in_progress') {
                        fd.append('actualDepartureDate', dateIso);
                    } else if (status === 'completed') {
                        fd.append('actualArrivalDate', dateIso);
                        if (!item.actualDate) {
                            fd.append('actualDepartureDate', dateIso);
                        }
                    }
                }

                if (finalNotes) {
                    fd.append('notes', finalNotes);
                }

                await OrderTrackingService.update(item.orderId, item.rawId, fd);
            }

            banner.add({
                key: 'update_status_success',
                variant: 'success',
                icon: faCheck,
                text: `Status successfully updated to ${status.toUpperCase()} for ${item.shipName || item.orderNumber || ''}`
            });

            onDismiss(true);
        } catch (e) {
            banner.add({
                key: 'update_status_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: 'Failed to update status. Error: ' + ErrorService.getMessage(e)
            });
            setSubmitting(false);
        }
    };

    return (
        <Panel
            headerText={`Update Operational Status`}
            isOpen={isOpen}
            type={PanelType.medium}
            onDismiss={() => onDismiss(false)}
            isFooterAtBottom={true}
            onRenderFooterContent={() => (
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    {!submitting ? (
                        <>
                            <PrimaryButton
                                text="Confirm & Update Status"
                                iconProps={{ iconName: 'CheckMark' }}
                                onClick={handleSubmit}
                            />
                            <DefaultButton text="Cancel" onClick={() => onDismiss(false)} />
                        </>
                    ) : (
                        <Spinner size={SpinnerSize.medium} label="Updating status..." labelPosition="right" />
                    )}
                </Stack>
            )}
        >
            <Stack tokens={{ childrenGap: 18 }} styles={{ root: { marginTop: 15 } }}>
                {/* Summary Info */}
                <Stack
                    styles={{
                        root: {
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: '14px 16px',
                        }
                    }}
                    tokens={{ childrenGap: 8 }}
                >
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <span
                            style={{
                                background: item.activityColor + '20',
                                color: item.activityColor,
                                border: `1px solid ${item.activityColor}40`,
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 700,
                            }}
                        >
                            <FontAwesomeIcon icon={item.activityIcon} style={{ marginRight: 5 }} />
                            {item.activityLabel}
                        </span>
                        <span
                            style={{
                                background: '#e2e8f0',
                                color: '#475569',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 600,
                            }}
                        >
                            Current Status: {item.statusLabel}
                        </span>
                    </Stack>

                    <Text size="large" style={{ fontWeight: 600, color: '#0f172a' }}>
                        {item.shipName ? `${item.shipName} ${item.voy ? `(Voy: ${item.voy})` : ''}` : item.orderNumber || '-'}
                    </Text>

                    {item.locationText && (
                        <Text size="small" style={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <FontAwesomeIcon icon={faLocationDot} style={{ color: '#94a3b8', fontSize: '11px' }} />
                            {item.locationText}
                        </Text>
                    )}
                </Stack>

                {/* Status Input */}
                <Stack tokens={{ childrenGap: 14 }}>
                    <Dropdown
                        label="Select New Status"
                        options={isShip ? SHIP_STATUS_OPTIONS : TRACKING_STATUS_OPTIONS}
                        selectedKey={status}
                        onChange={(_, opt) => {
                            if (opt) setStatus(opt.key as string);
                        }}
                        required
                    />

                    <DatePicker
                        label={isShip ? (status === 'sailing' ? 'Actual Departure Date (ATD)' : (status === 'arrived' || status === 'completed' ? 'Actual Arrival Date (ATA)' : 'Effective Date')) : (status === 'completed' ? 'Actual Completion Date' : 'Actual Start Date')}
                        value={actualDate}
                        onSelectDate={(date) => {
                            if (date) setActualDate(date);
                        }}
                        formatDate={GeneralService.formatDate}
                        isRequired
                    />

                    {/* Reschedule ETA input for sailing vessel to resolve overdue sailing */}
                    {isShip && status === 'sailing' && (
                        <DatePicker
                            label="New Estimated Arrival (ETA) - Reschedule if delayed"
                            value={newEstimatedArrival}
                            onSelectDate={(date) => {
                                if (date) setNewEstimatedArrival(date);
                            }}
                            formatDate={GeneralService.formatDate}
                        />
                    )}

                    {item.notes && item.notes.trim() !== '' && (
                        <div style={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            padding: '10px 12px',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                <FontAwesomeIcon icon={faClockRotateLeft} style={{ color: '#64748b', fontSize: 11 }} />
                                <span style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                    Riwayat Catatan Sebelumnya:
                                </span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#334155', whiteSpace: 'pre-line', maxHeight: '100px', overflowY: 'auto', lineHeight: 1.4 }}>
                                {item.notes}
                            </div>
                        </div>
                    )}

                    <TextField
                        label="Catatan Progres Baru (Opsional)"
                        multiline
                        rows={2}
                        value={notes}
                        onChange={(_, val) => setNotes(val || '')}
                        placeholder="e.g. Kapal antri sandar di dermaga / stuffing selesai jam 17:00..."
                        description="Catatan baru akan ditambahkan ke alur timeline proses tanpa menghapus riwayat catatan sebelumnya."
                    />
                </Stack>
            </Stack>
        </Panel>
    );
};

export default QuickStatusModal;
